// Account Settings → Email & Verification (1.27.70). Same behaviour as the list it
// replaces: resend for an unconfirmed email, backup email add/change/remove behind
// re-authentication, and the phone row PARKED.
//
// Phone number — PARKED 2026-08-12 (1.23.50). Sending a code needs a paid SMS
// provider, and the only alternative this build has is to print the code on
// screen, which is a prototype affordance and not something to hand a real
// member. So the row says "Coming soon" and offers nothing to click. Remove
// STAYS for anyone who added a number through the old prototype path: parking
// a feature must not trap a member with data they can no longer clear.
// Re-enabling is: restore Add/Change here, and set TWILIO_* (see backend/.env +
// accounts/sms.py).
import { useState } from 'react';
import { Button, useToast } from '@/ui';
import { useSession } from '@/hooks';
import type { User } from '@/core/models';
import {
  ContactVerifyDialog,
  RemoveContactDialog,
  type ContactKind,
} from '../../ContactVerifyDialog';
import { CardHead, ComingSoon, DashCard, DashRow, Pill } from '../../dashboard/DashCard';
import { RowList } from '../../dashboard/dashStyles';

export function EmailCard({ user }: { user: User }) {
  const { isEmailVerified, resendVerification } = useSession();
  const toast = useToast();
  const [resending, setResending] = useState(false);
  const [adding, setAdding] = useState<ContactKind | null>(null);
  const [removing, setRemoving] = useState<ContactKind | null>(null);

  async function resend() {
    setResending(true);
    try {
      await resendVerification();
      toast('Verification email sent. Check your inbox');
    } catch (e) {
      toast(
        e instanceof Error && e.name === 'RateLimited'
          ? 'Please wait a moment before requesting another email'
          : 'Could not resend right now. Please try again',
      );
    } finally {
      setResending(false);
    }
  }

  return (
    <DashCard labelledBy="acct-email-title" testId="email-card">
      <CardHead icon="mail" title="Email & Verification" titleId="acct-email-title" />
      <RowList>
        <DashRow
          icon="mail"
          label="Primary email"
          hint={isEmailVerified ? 'Confirmed' : 'Confirm it to post and interact'}
          testId="primary-email-row"
        >
          {isEmailVerified ? (
            <Pill tone="success" dot>
              Verified
            </Pill>
          ) : (
            <>
              <Pill>Unverified</Pill>
              <Button
                variant="secondary"
                size="sm"
                aria-label={resending ? undefined : 'Resend verification email'}
                onClick={() => void resend()}
                disabled={resending}
              >
                {resending ? 'Sending…' : 'Resend'}
              </Button>
            </>
          )}
        </DashRow>
        <DashRow
          icon="inbox"
          label="Backup email"
          hint={user.backupEmail || 'Not added. A second address for account recovery.'}
          testId="backup-email-row"
        >
          {user.backupEmail ? (
            <>
              {user.backupEmailVerified && (
                <Pill tone="success" dot>
                  Verified
                </Pill>
              )}
              <Button
                variant="secondary"
                size="sm"
                aria-label="Change backup email"
                onClick={() => setAdding('backup-email')}
              >
                Change
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove backup email"
                onClick={() => setRemoving('backup-email')}
              >
                Remove
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              aria-label="Add backup email"
              onClick={() => setAdding('backup-email')}
            >
              Add
            </Button>
          )}
        </DashRow>
        <DashRow
          icon="phone"
          label="Phone number"
          hint={user.phone || 'Not available yet'}
          testId="phone-row"
        >
          {user.phone ? (
            <>
              {user.phoneVerified && (
                <Pill tone="success" dot>
                  Verified
                </Pill>
              )}
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove phone number"
                onClick={() => setRemoving('phone')}
              >
                Remove
              </Button>
            </>
          ) : (
            <ComingSoon />
          )}
        </DashRow>
      </RowList>

      {adding && (
        <ContactVerifyDialog
          kind={adding}
          open
          onClose={() => setAdding(null)}
          onVerified={() => setAdding(null)}
        />
      )}
      {removing && <RemoveContactDialog kind={removing} open onClose={() => setRemoving(null)} />}
    </DashCard>
  );
}
